import React, { useEffect, useState, useRef } from 'react'
import { connect } from 'react-redux'
import Styles from './OverviewCard.module.css'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import {
  ChartComponent,
  OverviewInfoCard,
  IFText,
  IFSkeleton,
  IFSegmentedControl,
} from 'Components'
import AnalyticsSelectors from 'Stores/Analytics/Selectors'
import AnalyticsActions from 'Stores/Analytics/Actions'
import RequestState from 'Enums/RequestState'
import { useTranslation } from 'react-i18next'
import {
  getEnergyUnit,
  getMoneyUnit,
  formatXAxisDate,
  getSeriesLabelsByPeriod,
} from 'Utils/Calculations'
import { useApexResizeObserver } from 'Utils/Resizeobserver'

const OverviewCard = ({
  overview,
  fetchOverview,
  fetchOverviewRequestState,
  filter,
  currency,
}) => {
  const chartWrapperRef = useApexResizeObserver()
  const { t } = useTranslation()
  const selectedPeriod = filter.length > 1 ? filter[1]?.value : filter[0]?.value
  const [segmentedState, setSegmentedState] = useState(
    t('Analytics.SegmentedEnergy'),
  )
  useEffect(() => {
    fetchOverview(filter)
  }, [])

  useEffect(() => {
    fetchOverview(filter)
  }, [filter])

  const { current: currentLabel, previous: previousLabel } =
    getSeriesLabelsByPeriod(selectedPeriod)
  const seriesEnergy = [
    {
      name: currentLabel,
      type: 'area',
      data:
        overview.data?.energyGraph.current?.map((item) =>
          Math.ceil(item.energy),
        ) || [],
    },
    {
      name: previousLabel,
      type: 'line',
      data:
        overview.data?.energyGraph.previous?.map((item) =>
          Math.ceil(item.energy),
        ) || [],
    },
  ]

  const allValuesEnergy = seriesEnergy.flatMap((s) => s.data)
  const maxValueEnergy = Math.max(...allValuesEnergy)
  const { divisor: divisorEnergy, unit: unitEnergy } =
    getEnergyUnit(maxValueEnergy)

  const getLabelIndicesByPeriod = (dataArray, period) => {
    if (!dataArray || dataArray.length === 0) return []

    const indices = []
    const length = dataArray.length
    const pushIndex = (i) => {
      if (!indices.includes(i)) indices.push(i)
    }

    if (
      period === 'thisMonth' ||
      period === 'lastMonth' ||
      period === 'last3Months'
    ) {
      for (let i = 0; i < length; i += 7) {
        pushIndex(i)
      }
      return indices
    }

    if (period === 'last6Months') {
      dataArray.forEach((item, index) => {
        const date = new Date(item.date)
        const day = date.getDate()

        if (day === 1 || day === 15) {
          pushIndex(index)
        }
      })
      return indices
    }
    dataArray.forEach((item, index) => {
      const date = new Date(item.date)
      if (date.getDate() === 1) {
        pushIndex(index)
      }
    })
    return indices
  }
  const monthStartIndices =
    overview.data?.energyGraph?.current?.length == 0
      ? getLabelIndicesByPeriod(
          overview.data?.energyGraph.previous,
          selectedPeriod,
        )
      : getLabelIndicesByPeriod(
          overview.data?.energyGraph.current,
          selectedPeriod,
        )

  const optionsEnergy = {
    chart: {
      id: 'energy-consumption',
      toolbar: { show: false },
    },
    colors: [Colors.primary, Colors.Analytics.Icons],
    stroke: {
      curve: 'smooth',
      width: [3, 2],
      dashArray: [0, 4],
    },
    fill: {
      type: ['gradient', 'none'],
      gradient: {
        shade: 'light',
        shadeIntensity: 0.2,
        gradientToColors: [Colors.Analytics.GraphGradient],
        opacityFrom: 0.6,
        opacityTo: 0.1,
        stops: [0, 100],
        inverseColors: false,
        type: 'vertical',
      },
    },
    xaxis: {
      categories:
        overview.data?.energyGraph.current?.length > 0
          ? overview.data?.energyGraph.current?.map((item, index) => {
              const label = formatXAxisDate(item.date, selectedPeriod)
              return monthStartIndices.includes(index) ? label : ''
            }) || []
          : overview.data?.energyGraph.previous?.map((item, index) => {
              const label = formatXAxisDate(item.date, selectedPeriod)
              return monthStartIndices.includes(index) ? label : ''
            }) || [],
      labels: {
        rotate: -45,
        hideOverlappingLabels: false,
      },
    },
    yaxis: {
      min: 0,
      labels: {
        formatter: (value) =>
          `${Math.ceil(value / divisorEnergy)} ${unitEnergy}`,
      },
    },
    tooltip: {
      x: {
        formatter: (_, { dataPointIndex }) => {
          const item =
            overview.data?.energyGraph.current?.[dataPointIndex] ||
            overview.data?.energyGraph.previous?.[dataPointIndex]
          return item?.date ? formatXAxisDate(item.date, selectedPeriod) : ''
        },
      },
    },
    dataLabels: { enabled: false },
    grid: { show: false },
  }

  const seriesRev = [
    {
      name: currentLabel,
      type: 'area',
      data:
        overview.data?.revenueGraph.current?.map((item) =>
          Math.ceil(item.revenue),
        ) || [],
    },
    {
      name: previousLabel,
      type: 'line',
      data:
        overview.data?.revenueGraph.previous?.map((item) =>
          Math.ceil(item.revenue),
        ) || [],
    },
  ]
  const allValuesRev = seriesRev.flatMap((s) => s.data)
  const maxValueRev = Math.max(...allValuesRev)
  const { divisor: divisorRev, unit: unitRev } = getMoneyUnit(maxValueRev)

  const optionsRev = {
    chart: {
      id: 'energy-consumption',
      toolbar: { show: false },
    },
    colors: [Colors.primary, Colors.Analytics.Icons],
    stroke: {
      curve: 'smooth',
      width: [3, 2],
      dashArray: [0, 4],
    },
    fill: {
      type: ['gradient', 'none'],
      gradient: {
        shade: 'light',
        shadeIntensity: 0.2,
        gradientToColors: [Colors.Analytics.GraphGradient],
        opacityFrom: 0.6,
        opacityTo: 0.1,
        stops: [0, 100],
        inverseColors: false,
        type: 'vertical',
      },
    },

    xaxis: {
      categories:
        overview.data?.revenueGraph.current?.length > 0
          ? overview.data?.revenueGraph.current?.map((item, index) => {
              const label = formatXAxisDate(item.date, selectedPeriod)
              return monthStartIndices.includes(index) ? label : ''
            }) || []
          : overview.data?.revenueGraph.previous?.map((item, index) => {
              const label = formatXAxisDate(item.date, selectedPeriod)
              return monthStartIndices.includes(index) ? label : ''
            }) || [],
      labels: {
        rotate: -45,
        hideOverlappingLabels: false,
      },
    },
    yaxis: {
      min: 0,
      labels: {
        formatter: (value) =>
          `${Math.ceil(value / divisorRev)} ${unitRev}${currency}`,
      },
    },
    tooltip: {
      x: {
        formatter: (_, { dataPointIndex }) => {
          const item =
            overview.data?.revenueGraph.current?.[dataPointIndex] ||
            overview.data?.revenueGraph.previous?.[dataPointIndex]
          return item?.date ? formatXAxisDate(item.date, selectedPeriod) : ''
        },
      },
    },
    dataLabels: { enabled: false },
    grid: { show: false },
  }

  useEffect(() => {
    setTimeout(() => {
      window.ApexCharts?.exec('energy-consumption', 'resize')
    }, 100)
  }, [])
  return (
    <div
      style={{
        backgroundColor: Colors.white,
      }}
      className={Styles.Card}
    >
      <IFSegmentedControl
        name="group-1"
        isLoading={fetchOverviewRequestState === RequestState.LOADING}
        callback={(val) => setSegmentedState(val)}
        controlRef={useRef()}
        segments={[
          {
            label: t('Analytics.SegmentedEnergy'),
            value: t('Analytics.SegmentedEnergy'),
            ref: useRef(),
          },
          {
            label: t('Analytics.SegmentedRevenue'),
            value: t('Analytics.SegmentedRevenue'),
            ref: useRef(),
          },
        ]}
      />
      <OverviewInfoCard
        chargingSessions={overview.data?.stats.sessions.value}
        chargingSessionsPercentage={overview.data?.stats.sessions.change}
        energyConsumed={overview.data?.stats.consumed.value}
        energyConsumedPercentage={overview.data?.stats.consumed.change}
        averageConsumption={overview.data?.stats.avgConsumption.value}
        averageConsumptionPercentage={
          overview.data?.stats.avgConsumption.change
        }
        averageDuration={overview.data?.stats.avgDuration.value}
        averageDurationPercentage={overview.data?.stats.avgDuration.change}
        isLoading={fetchOverviewRequestState === RequestState.LOADING}
        isRevenue={segmentedState === t('Analytics.SegmentedRevenue')}
        currency={currency}
        averageCost={overview.data?.stats.avgChargeCost.value}
        averageCostPercentage={overview.data?.stats.avgChargeCost.change}
        transactionRevenue={overview.data?.stats.transactionRevenue.value}
        transactionRevenuePercentage={
          overview.data?.stats.transactionRevenue.change
        }
      />

      <div className={Styles.GraphContainer}>
        {fetchOverviewRequestState === RequestState.LOADING ? (
          <div className={Styles.ChartSkeleton}>
            <IFSkeleton width={'100%'} height={'100%'} />
          </div>
        ) : (
          <div className={Styles.Chart} ref={chartWrapperRef}>
            <ChartComponent
              options={
                segmentedState === t('Analytics.SegmentedRevenue')
                  ? optionsRev
                  : optionsEnergy
              }
              series={
                segmentedState === t('Analytics.SegmentedRevenue')
                  ? seriesRev
                  : seriesEnergy
              }
              height="100%"
              width="100%"
              type="area"
              className={
                (segmentedState === t('Analytics.SegmentedRevenue')
                  ? !seriesRev[0].data ||
                    seriesRev[0].data.length === 0 ||
                    seriesRev[0].data.every((val) => val === 0)
                  : !seriesEnergy[0].data ||
                    seriesEnergy[0].data.length === 0 ||
                    seriesEnergy[0].data.every((val) => val === 0)) ||
                !(
                  fetchOverviewRequestState === RequestState.LOADING ||
                  fetchOverviewRequestState === RequestState.SUCCEEDED ||
                  fetchOverviewRequestState == RequestState.UNINITIALIZED
                )
                  ? Styles.OverlayTest
                  : ''
              }
            />

            {(
              segmentedState === t('Analytics.SegmentedRevenue')
                ? !seriesRev[0].data ||
                  seriesRev[0].data.length === 0 ||
                  seriesRev[0].data.every((val) => val === 0)
                : !seriesEnergy[0].data ||
                  seriesEnergy[0].data.length === 0 ||
                  seriesEnergy[0].data.every((val) => val === 0) ||
                  !(
                    fetchOverviewRequestState === RequestState.LOADING ||
                    fetchOverviewRequestState === RequestState.SUCCEEDED ||
                    fetchOverviewRequestState == RequestState.UNINITIALIZED
                  )
            ) ? (
              <IFText
                className={Styles.OverlayText}
                style={{ color: Colors.red }}
              >
                {t('Analytics.NoData')}
              </IFText>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  overview: AnalyticsSelectors.getOverview(state),
  fetchOverviewRequestState:
    AnalyticsSelectors.getFetchOverviewRequestState(state),
})
function mapDispatchToProps(dispatch) {
  return {
    fetchOverview: (filter) => dispatch(AnalyticsActions.fetchOverview(filter)),
  }
}
OverviewCard.propTypes = {
  filter: PropTypes.object,
  fetchOverview: PropTypes.func,
  fetchOverviewRequestState: PropTypes.number,
  overview: PropTypes.object,
  currency: PropTypes.string,
}

export default connect(mapStateToProps, mapDispatchToProps)(OverviewCard)
